<script setup>
import { computed, ref } from "vue";
import { useMainStore } from "@/stores/main.js";
import MSheet from "@/components/m/MSheet.vue";

const mainStore = useMainStore();

const locations = computed(() =>
  (mainStore.locations || []).filter((l) => l.location_id !== "ALL"),
);

const selectedId = computed(() => {
  const l = mainStore.selectedLocation;
  if (!l || l.location_id === "ALL") return null;
  return l.location_id;
});

const selectedEntity = computed(() => {
  if (!selectedId.value) return null;
  return (
    locations.value.find(
      (l) => String(l.location_id) === String(selectedId.value),
    ) || null
  );
});

const allLabel = "All locations";
const allCount = computed(() => locations.value.length);

const open = ref(false);
const q = ref("");

function pick(location) {
  if (!location) {
    mainStore.setSelectedLocation({
      id: "ALL",
      location_id: "ALL",
      name: "All locations",
      label: "All locations",
    });
  } else {
    mainStore.setSelectedLocation(location);
  }
  open.value = false;
}

function clearAll() {
  mainStore.setSelectedLocation({
    id: "ALL",
    location_id: "ALL",
    name: "All locations",
    label: "All locations",
  });
}

const groups = computed(() => {
  const s = q.value.trim().toLowerCase();
  const order = [];
  const by = {};
  locations.value.forEach((l) => {
    const g = l.group || l.region || l.area || "";
    if (!(g in by)) {
      by[g] = [];
      order.push(g);
    }
    by[g].push(l);
  });
  return order
    .map((g) => ({
      name: g,
      items: by[g].filter(
        (l) =>
          !s ||
          (l.label || l.name || "").toLowerCase().includes(s) ||
          g.toLowerCase().includes(s),
      ),
    }))
    .filter((grp) => grp.items.length);
});

const scoreColor = (n) =>
  n >= 75 ? "var(--pos)" : n >= 62 ? "var(--brand-strong)" : "var(--neg)";
</script>

<template>
  <div v-if="mainStore.usesLocations" class="lsm">
    <!-- chip bar -->
    <div class="lsm-bar">
      <button
        class="lsm-chip"
        :class="{ active: !!selectedEntity }"
        @click="open = true"
      >
        <span class="lsm-chip-ic">
          <!-- pin -->
          <svg
            v-if="selectedEntity"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
          <!-- globe -->
          <svg
            v-else
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
          </svg>
        </span>
        <span class="lsm-chip-t">{{
          selectedEntity
            ? selectedEntity.label || selectedEntity.name
            : allLabel
        }}</span>
        <span class="lsm-chip-s">{{
          selectedEntity
            ? selectedEntity.group || selectedEntity.region || ""
            : allCount + " locations"
        }}</span>
        <!-- chevron -->
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="color: var(--text-3); flex: 0 0 auto"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      <button
        v-if="selectedEntity"
        class="lsm-clear"
        title="View all"
        @click="clearAll"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>

    <!-- bottom sheet -->
    <MSheet :open="open" @close="open = false">
      <div class="lsm-sheet-head">
        <div class="lsm-sheet-title">Location</div>
        <span class="lsm-sheet-count">{{ allCount }} locations</span>
      </div>
      <div class="lsm-search">
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="color: var(--text-3); flex: 0 0 auto"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input v-model="q" placeholder="Search locations…" inputmode="search" />
      </div>
      <div class="lsm-list">
        <!-- all row -->
        <button
          class="lsm-srow"
          :class="{ on: !selectedEntity }"
          @click="pick(null)"
        >
          <span class="lsm-srow-ic">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
            </svg>
          </span>
          <div class="lsm-srow-txt">
            <div class="lsm-srow-name">{{ allLabel }}</div>
            <div class="lsm-srow-sub">
              {{ allCount }} locations · whole company
            </div>
          </div>
          <span class="lsm-srow-ck" :class="{ on: !selectedEntity }">
            <svg
              v-if="!selectedEntity"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </button>

        <template v-for="g in groups" :key="g.name">
          <div v-if="g.name" class="lsm-grouplabel">{{ g.name }}</div>
          <button
            v-for="l in g.items"
            :key="l.location_id"
            class="lsm-srow"
            :class="{ on: String(selectedId) === String(l.location_id) }"
            @click="pick(l)"
          >
            <span class="lsm-srow-ic">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
                <circle cx="12" cy="10" r="2.5" />
              </svg>
            </span>
            <div class="lsm-srow-txt">
              <div class="lsm-srow-name">{{ l.label || l.name }}</div>
              <div class="lsm-srow-sub">{{ l.group || l.region || "" }}</div>
            </div>
            <div v-if="l.score != null" class="lsm-srow-meta">
              <b :style="{ color: scoreColor(l.score) }">{{ l.score }}</b>
              <i v-if="l.rating != null">{{ Number(l.rating).toFixed(2) }}★</i>
            </div>
            <span
              class="lsm-srow-ck"
              :class="{ on: String(selectedId) === String(l.location_id) }"
            >
              <svg
                v-if="String(selectedId) === String(l.location_id)"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
          </button>
        </template>

        <div v-if="!groups.length && q" class="lsm-empty">
          No location matches "{{ q }}".
        </div>
      </div>
    </MSheet>
  </div>
</template>

<style scoped>
.lsm {
  font-family: var(--ui);
}
.lsm * {
  box-sizing: border-box;
}

/* chip bar */
.lsm-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.lsm-chip {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 13px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  cursor: pointer;
  color: var(--text);
  font-family: var(--ui);
}
.lsm-chip.active {
  background: var(--brand-soft);
  border-color: var(--brand-line);
}
.lsm-chip-ic {
  display: grid;
  place-items: center;
  color: var(--text-3);
  flex: 0 0 auto;
}
.lsm-chip.active .lsm-chip-ic {
  color: var(--brand-strong);
}
.lsm-chip-t {
  font-weight: 800;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.lsm-chip-s {
  font: 600 10.5px var(--mono);
  color: var(--text-3);
  margin-left: auto;
  padding-left: 9px;
  border-left: 1px solid var(--border-2);
  white-space: nowrap;
  flex: 0 0 auto;
}
.lsm-chip.active .lsm-chip-s {
  color: var(--brand-strong);
  border-color: var(--brand-line);
}
.lsm-clear {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 13px;
  display: grid;
  place-items: center;
  background: var(--brand-soft);
  border: 1px solid var(--brand-line);
  color: var(--brand-strong);
  cursor: pointer;
  font-family: var(--ui);
}

/* sheet contents (rendered inside MSheet's slot) */
.lsm-sheet-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.lsm-sheet-title {
  font-size: 19px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--text);
}
.lsm-sheet-count {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-3);
}
.lsm-search {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 44px;
  padding: 0 13px;
  margin-bottom: 14px;
  border-radius: 13px;
  background: var(--surface-2);
  border: 1px solid var(--border);
}
.lsm-search input {
  flex: 1;
  border: 0;
  outline: 0;
  background: transparent;
  font: 400 15px var(--ui);
  color: var(--text);
}
.lsm-list {
  overflow-y: auto;
}
.lsm-grouplabel {
  font: 600 9.5px var(--mono);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-3);
  margin: 12px 4px 8px;
}
.lsm-srow {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 13px;
  margin-bottom: 8px;
  cursor: pointer;
  border-radius: 15px;
  border: 1px solid var(--border);
  background: var(--surface);
  text-align: left;
  font-family: var(--ui);
  color: var(--text);
}
.lsm-srow.on {
  border-color: var(--brand-line);
  background: var(--brand-soft);
}
.lsm-srow-ic {
  width: 40px;
  height: 40px;
  border-radius: 13px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  background: var(--surface-3);
  color: var(--text-3);
}
.lsm-srow-txt {
  flex: 1;
  min-width: 0;
}
.lsm-srow-name {
  font-weight: 800;
  font-size: 14.5px;
  color: var(--text);
}
.lsm-srow-sub {
  font-size: 12px;
  color: var(--text-3);
}
.lsm-srow-meta {
  text-align: right;
  margin-right: 2px;
}
.lsm-srow-meta b {
  display: block;
  font: 800 15px var(--mono);
}
.lsm-srow-meta i {
  font: 600 8.5px var(--mono);
  letter-spacing: 0.1em;
  color: var(--text-3);
  font-style: normal;
}
.lsm-srow-ck {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border: 1.5px solid var(--border-2);
  color: transparent;
}
.lsm-srow-ck.on {
  background: var(--brand);
  border-color: var(--brand);
  color: #2a1c00;
}
.lsm-empty {
  padding: 20px;
  text-align: center;
  color: var(--text-3);
  font-size: 13px;
}
</style>
